import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { alumnosProfesorService } from "../../../services/profesor/alumnos.service";
import { Notification } from "../../../components/Notification";

interface CargaMasivaAlumnosProps {
  onClose: () => void;
  onSuccess: () => void;
  aniosEscolares: Array<{ idAnioEscolar: number; nombre: string }>;
  cursos: Array<{
    idCurso: number;
    nombre: string;
    anioEscolarId: number;
    AnioEscolar: { nombre: string };
  }>;
}

interface AlumnoPreview {
  nombres: string;
  apellidos: string;
  email?: string;
  rut?: string;
}

export const CargaMasivaAlumnos: React.FC<CargaMasivaAlumnosProps> = ({
  onClose,
  onSuccess,
  aniosEscolares,
  cursos,
}) => {
  const [cursoId, setCursoId] = useState<number | null>(null);
  const [anioEscolarId, setAnioEscolarId] = useState<number | null>(null);
  const [procesando, setProcesando] = useState(false);
  const [alumnosManuales, setAlumnosManuales] = useState<AlumnoPreview[]>([
    { nombres: "", apellidos: "", email: "", rut: "" },
  ]);
  const [resultado, setResultado] = useState<{
    exitosos: Array<{
      nombres: string;
      apellidos: string;
      email: string;
      passwordTemporal: string;
    }>;
    errores: Array<{
      nombres: string;
      apellidos: string;
      email?: string;
      error: string;
    }>;
    resumen: {
      total: number;
      exitosos: number;
      errores: number;
    };
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    type: "success" | "error" | "info" | "warning";
    title: string;
    message: string;
  } | null>(null);

  const cursosFiltrados = anioEscolarId
    ? cursos.filter((c) => c.anioEscolarId === anioEscolarId)
    : cursos;

  const parsearCSV = (texto: string): AlumnoPreview[] => {
    const lineas = texto.split("\n").filter((linea) => linea.trim());
    if (lineas.length === 0) return [];

    const alumnos: AlumnoPreview[] = [];
    const tieneEncabezados =
      lineas[0].toLowerCase().includes("nombre") ||
      lineas[0].toLowerCase().includes("email");

    const inicio = tieneEncabezados ? 1 : 0;

    for (let i = inicio; i < lineas.length; i++) {
      const columnas = lineas[i]
        .split(",")
        .map((col) => col.trim().replace(/^"|"$/g, ""));

      if (columnas.length >= 2) {
        const nombres = (columnas[0] || "").trim();
        const apellidos = (columnas[1] || "").trim();

        if (nombres && apellidos) {
          alumnos.push({
            nombres,
            apellidos,
            email: columnas[2] ? columnas[2].trim() : undefined,
            rut: columnas[3] ? columnas[3].trim() : undefined,
          });
        }
      }
    }

    return alumnos;
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const esExcel =
      file.name.endsWith(".xlsx") ||
      file.name.endsWith(".xls") ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel";

    if (esExcel) {
      try {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        }) as any[][];

        if (jsonData.length === 0) {
          setNotification({
            isOpen: true,
            type: "warning",
            title: "Archivo vacío",
            message: "El archivo Excel está vacío",
          });
          return;
        }

        const tieneEncabezados =
          jsonData[0] &&
          (jsonData[0][0]?.toString().toLowerCase().includes("nombre") ||
            jsonData[0][0]?.toString().toLowerCase().includes("nombres"));

        const inicio = tieneEncabezados ? 1 : 0;
        const alumnosParseados: AlumnoPreview[] = [];

        for (let i = inicio; i < jsonData.length; i++) {
          const fila = jsonData[i];
          if (!fila || fila.length < 2) continue;

          const nombres = (fila[0]?.toString() || "").trim();
          const apellidos = (fila[1]?.toString() || "").trim();

          if (nombres && apellidos) {
            alumnosParseados.push({
              nombres,
              apellidos,
              email: fila[2] ? (fila[2]?.toString() || "").trim() : undefined,
              rut: fila[3] ? (fila[3]?.toString() || "").trim() : undefined,
            });
          }
        }

        if (alumnosParseados.length > 0) {
          setAlumnosManuales(alumnosParseados);
        } else {
          setNotification({
            isOpen: true,
            type: "warning",
            title: "Datos no válidos",
            message: "No se encontraron datos válidos en el archivo. Asegúrate de que tenga al menos nombres y apellidos.",
          });
          setAlumnosManuales([
            { nombres: "", apellidos: "", email: "", rut: "" },
          ]);
        }
      } catch (error) {
        console.error("Error al leer Excel:", error);
        setNotification({
          isOpen: true,
          type: "error",
          title: "Error al leer archivo",
          message: "Error al leer el archivo Excel. Asegúrate de que tenga el formato correcto.",
        });
      }
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        const texto = e.target?.result as string;
        const alumnosParseados = parsearCSV(texto);
        if (alumnosParseados.length > 0) {
          setAlumnosManuales(alumnosParseados);
        } else {
          setNotification({
            isOpen: true,
            type: "warning",
            title: "Datos no válidos",
            message: "No se encontraron datos válidos en el archivo. Asegúrate de que tenga al menos nombres y apellidos.",
          });
          setAlumnosManuales([
            { nombres: "", apellidos: "", email: "", rut: "" },
          ]);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleProcesar = async () => {
    const alumnosValidos = alumnosManuales.filter(
      (a) => a.nombres.trim() && a.apellidos.trim()
    );

    if (alumnosValidos.length === 0) {
      setNotification({
        isOpen: true,
        type: "warning",
        title: "Datos incompletos",
        message: "No hay alumnos para procesar. Completa al menos nombres y apellidos.",
      });
      return;
    }

    if (!cursoId && !anioEscolarId) {
      setNotification({
        isOpen: true,
        type: "warning",
        title: "Campo requerido",
        message: "Debes seleccionar un curso o año escolar",
      });
      return;
    }

    setProcesando(true);
    try {
      const response = await alumnosProfesorService.createAlumnosBulk({
        alumnos: alumnosValidos,
        cursoId: cursoId || null,
        anioEscolarId: anioEscolarId || null,
        vigente: true,
      });

      setResultado({
        exitosos: response.data.exitosos,
        errores: response.data.errores,
        resumen: response.resumen,
      });
    } catch (error: any) {
      setNotification({
        isOpen: true,
        type: "error",
        title: "Error al procesar",
        message: error.response?.data?.error || error.message || "Error desconocido al procesar los alumnos",
      });
    } finally {
      setProcesando(false);
    }
  };

  const handleAnioEscolarChange = (anioId: number | null) => {
    setAnioEscolarId(anioId);
    setCursoId(null);
  };

  const agregarFilaManual = () => {
    setAlumnosManuales([
      ...alumnosManuales,
      { nombres: "", apellidos: "", email: "", rut: "" },
    ]);
  };

  const eliminarFilaManual = (index: number) => {
    const nuevasFilas = alumnosManuales.filter((_, i) => i !== index);
    setAlumnosManuales(nuevasFilas);
    if (nuevasFilas.length === 0) {
      setAlumnosManuales([{ nombres: "", apellidos: "", email: "", rut: "" }]);
    }
  };

  const actualizarFilaManual = (
    index: number,
    campo: keyof AlumnoPreview,
    valor: string
  ) => {
    const nuevasFilas = [...alumnosManuales];
    nuevasFilas[index] = { ...nuevasFilas[index], [campo]: valor };
    setAlumnosManuales(nuevasFilas);
  };

  const descargarEjemploCSV = () => {
    const datosEjemplo = [
      ["nombres", "apellidos", "email", "rut"],
      ["Juan", "Pérez", "juan.perez@demo.fracto.cl", "12345678-9"],
      ["María", "González", "maria.gonzalez@demo.fracto.cl", "98765432-1"],
      ["Carlos", "Rodríguez", "carlos.rodriguez@demo.fracto.cl", "11223344-5"],
      ["Ana", "Martínez", "ana.martinez@demo.fracto.cl", "55667788-9"],
      ["Luis", "Sánchez", "luis.sanchez@demo.fracto.cl", "99887766-5"],
      ["Pedro", "Morales", "pedro.morales@demo.fracto.cl", "22334455-6"],
      ["Laura", "Fernández", "laura.fernandez@demo.fracto.cl", "33445566-7"],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(datosEjemplo);

    const columnWidths = [{ wch: 15 }, { wch: 15 }, { wch: 30 }, { wch: 12 }];
    worksheet["!cols"] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Alumnos");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "ejemplo_alumnos.xlsx");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  Carga de Alumnos
                </h2>
                <p className="text-purple-100 text-sm">
                  Sube un archivo CSV/Excel o completa los datos manualmente
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1">
            {!resultado ? (
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Subir archivo CSV/Excel
                    </label>
                    <button
                      onClick={descargarEjemploCSV}
                      className="text-xs text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1 underline"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                        />
                      </svg>
                      Descargar ejemplo CSV
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.txt,.xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full px-4 py-3 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-500 transition-colors text-purple-700 font-medium"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      Seleccionar archivo CSV/Excel
                    </div>
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    Formato: nombres,apellidos,email,rut (una línea por alumno).
                    Los datos se mostrarán en casillas para revisión y edición.
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-semibold text-gray-700">
                      Completar estudiantes
                    </label>
                    <button
                      onClick={agregarFilaManual}
                      className="px-3 py-1 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Agregar fila
                    </button>
                  </div>
                  <div className="space-y-3 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
                    {alumnosManuales.map((alumno, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <span className="text-xs font-semibold text-gray-600">
                            Estudiante {index + 1}
                          </span>
                          {alumnosManuales.length > 1 && (
                            <button
                              onClick={() => eliminarFilaManual(index)}
                              className="text-red-600 hover:text-red-800 transition-colors"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Nombres *
                            </label>
                            <input
                              type="text"
                              value={alumno.nombres}
                              onChange={(e) =>
                                actualizarFilaManual(
                                  index,
                                  "nombres",
                                  e.target.value
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                              placeholder="Juan"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Apellidos *
                            </label>
                            <input
                              type="text"
                              value={alumno.apellidos}
                              onChange={(e) =>
                                actualizarFilaManual(
                                  index,
                                  "apellidos",
                                  e.target.value
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                              placeholder="Pérez"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Email (opcional)
                            </label>
                            <input
                              type="email"
                              value={alumno.email || ""}
                              onChange={(e) =>
                                actualizarFilaManual(
                                  index,
                                  "email",
                                  e.target.value
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                              placeholder="juan@email.com"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              RUT (opcional)
                            </label>
                            <input
                              type="text"
                              value={alumno.rut || ""}
                              onChange={(e) =>
                                actualizarFilaManual(
                                  index,
                                  "rut",
                                  e.target.value
                                )
                              }
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                              placeholder="12345678-9"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Año Escolar
                    </label>
                    <select
                      value={anioEscolarId || ""}
                      onChange={(e) =>
                        handleAnioEscolarChange(
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                      disabled={aniosEscolares.length === 0}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                    >
                      {aniosEscolares.length === 0 ? (
                        <option value="">No hay registros disponibles</option>
                      ) : (
                        <>
                          <option value="">Seleccione un año escolar</option>
                          {aniosEscolares.map((anio) => (
                            <option
                              key={anio.idAnioEscolar}
                              value={anio.idAnioEscolar}
                            >
                              {anio.nombre}
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Curso (opcional)
                    </label>
                    <select
                      value={cursoId || ""}
                      onChange={(e) =>
                        setCursoId(
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                      disabled={!anioEscolarId}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white disabled:bg-gray-50 disabled:text-gray-500"
                    >
                      <option value="">
                        {anioEscolarId
                          ? "Seleccione un curso"
                          : "Seleccione año escolar primero"}
                      </option>
                      {cursosFiltrados.map((curso) => (
                        <option key={curso.idCurso} value={curso.idCurso}>
                          {curso.nombre} ({curso.AnioEscolar.nombre})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-green-900 mb-2">
                    Resultado de la carga masiva
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-700">
                        {resultado.resumen.total}
                      </p>
                      <p className="text-sm text-green-600">Total</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-700">
                        {resultado.resumen.exitosos}
                      </p>
                      <p className="text-sm text-green-600">Exitosos</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-700">
                        {resultado.resumen.errores}
                      </p>
                      <p className="text-sm text-red-600">Errores</p>
                    </div>
                  </div>
                </div>

                {resultado.exitosos.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      Alumnos creados exitosamente ({resultado.exitosos.length})
                    </h4>
                    <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-3 py-2">Nombre</th>
                            <th className="text-left px-3 py-2">Email</th>
                            <th className="text-left px-3 py-2">Contraseña</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resultado.exitosos.map((alumno, index) => (
                            <tr
                              key={index}
                              className="border-t border-gray-200 hover:bg-gray-50"
                            >
                              <td className="px-3 py-2">
                                {alumno.nombres} {alumno.apellidos}
                              </td>
                              <td className="px-3 py-2">{alumno.email}</td>
                              <td className="px-3 py-2 font-mono">
                                {alumno.passwordTemporal}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {resultado.errores.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-red-700 mb-2">
                      Errores ({resultado.errores.length})
                    </h4>
                    <div className="max-h-60 overflow-y-auto border border-red-200 rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-red-50">
                          <tr>
                            <th className="text-left px-3 py-2">Nombre</th>
                            <th className="text-left px-3 py-2">Email</th>
                            <th className="text-left px-3 py-2">Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resultado.errores.map((error, index) => (
                            <tr
                              key={index}
                              className="border-t border-red-200 hover:bg-red-50"
                            >
                              <td className="px-3 py-2">
                                {error.nombres} {error.apellidos}
                              </td>
                              <td className="px-3 py-2">
                                {error.email || "-"}
                              </td>
                              <td className="px-3 py-2 text-red-700">
                                {error.error}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
            {!resultado ? (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleProcesar}
                  disabled={
                    alumnosManuales.filter(
                      (a) => a.nombres.trim() && a.apellidos.trim()
                    ).length === 0 || procesando
                  }
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {procesando ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Procesar{" "}
                      {
                        alumnosManuales.filter(
                          (a) => a.nombres.trim() && a.apellidos.trim()
                        ).length
                      }{" "}
                      alumno(s)
                    </>
                  )}
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  onSuccess();
                  onClose();
                }}
                className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-colors font-medium"
              >
                Cerrar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notificación */}
      {notification && (
        <Notification
          isOpen={notification.isOpen}
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};
